import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Loader2, DollarSign, Download, RefreshCcw, CreditCard, Clock, Gift, Crown, BookOpen } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { cn } from '../../lib/utils'
import type { Transaction, TransactionType, TransactionStatus } from '../../types'

export function TransactionHistory() {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<TransactionType | 'all'>('all')

  useEffect(() => {
    loadTransactions()
  }, [filter])

  const loadTransactions = async () => {
    try {
      setLoading(true)
      const response = await apiClient.transactions.getUserTransactions(
        50,
        0,
        filter === 'all' ? undefined : filter
      )

      if (response.success) {
        setTransactions(response.transactions)
      }
    } catch (error) {
      console.error('Error loading transactions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: TransactionStatus | string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', text: string }> = {
      completed: { variant: 'default', text: 'Completed' },
      pending: { variant: 'secondary', text: 'Pending' },
      failed: { variant: 'destructive', text: 'Failed' },
      refunded: { variant: 'outline', text: 'Refunded' },
    }

    const config = variants[status] || { variant: 'secondary' as const, text: status || 'Unknown' }
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.text}
      </Badge>
    )
  }

  // Parse extra_data safely
  const parseExtraData = (extraData: string | null | undefined): Record<string, any> => {
    if (!extraData) return {}
    try {
      return typeof extraData === 'string' ? JSON.parse(extraData) : extraData
    } catch {
      return {}
    }
  }

  // Check if this is a bonus downloads reward
  const isBonusDownloadReward = (transaction: Transaction): boolean => {
    const extraData = parseExtraData(transaction.extra_data)
    return transaction.type === 'reward' && extraData.reward_type === 'bonus_downloads'
  }

  // Check if this is a referral-related transaction
  const isReferralRelated = (transaction: Transaction): boolean => {
    const extraData = parseExtraData(transaction.extra_data)
    return isBonusDownloadReward(transaction) || extraData.source_referral_id != null
  }

  // Get icon based on transaction type and extra data
  const getTypeIcon = (transaction: Transaction) => {
    const type = transaction.type
    const extraData = parseExtraData(transaction.extra_data)

    // Special handling for rewards
    if (type === 'reward') {
      if (extraData.reward_type === 'bonus_downloads') {
        return <Download className="h-4 w-4 text-purple-500" />
      }
      return <Gift className="h-4 w-4 text-emerald-500" />
    }

    // Icon mapping for other types
    const icons: Record<string, React.ReactNode> = {
      translation: <BookOpen className="h-4 w-4 text-blue-500" />,
      subscription: <Crown className="h-4 w-4 text-yellow-500" />,
      download: <Download className="h-4 w-4 text-cyan-500" />,
      refund: <RefreshCcw className="h-4 w-4 text-orange-500" />,
      credit_purchase: <CreditCard className="h-4 w-4 text-green-500" />,
    }

    return icons[type] || <DollarSign className="h-4 w-4" />
  }

  // Get background color for icon wrapper
  const getIconBgClass = (transaction: Transaction): string => {
    const type = transaction.type

    if (type === 'reward') {
      if (isBonusDownloadReward(transaction)) {
        return 'bg-purple-500/10'
      }
      return 'bg-emerald-500/10'
    }

    const bgClasses: Record<string, string> = {
      translation: 'bg-blue-500/10',
      subscription: 'bg-yellow-500/10',
      download: 'bg-cyan-500/10',
      refund: 'bg-orange-500/10',
      credit_purchase: 'bg-green-500/10',
    }

    return bgClasses[type] || 'bg-muted'
  }

  // Get display name for transaction type
  const getTypeName = (transaction: Transaction): string => {
    const type = transaction.type
    const extraData = parseExtraData(transaction.extra_data)

    if (type === 'reward') {
      if (extraData.reward_type === 'bonus_downloads') {
        return 'Bonus Takes'
      }
      return 'Credit Reward'
    }

    const names: Record<string, string> = {
      translation: 'Translation',
      subscription: 'Subscription',
      download: 'Download',
      refund: 'Refund',
      credit_purchase: 'Credit Purchase',
      credit_purchase_refund: 'Credit Refund',
    }

    return names[type] || type || 'Unknown'
  }

  // Get the display value (amount or downloads count)
  const getDisplayValue = (transaction: Transaction): { text: string; isPositive: boolean } => {
    const type = transaction.type
    const extraData = parseExtraData(transaction.extra_data)

    // For bonus downloads, show download count instead of amount
    if (type === 'reward' && extraData.reward_type === 'bonus_downloads') {
      const downloadAmount = extraData.bonus_download_amount || 0
      return {
        text: `+${downloadAmount} downloads`,
        isPositive: true,
      }
    }

    // For other rewards and refunds, show as positive
    if (type === 'reward' || type === 'refund') {
      return {
        text: `+$${transaction.amount.toFixed(2)}`,
        isPositive: true,
      }
    }

    // For credit purchases (money added to wallet)
    if (type === 'credit_purchase') {
      const creditAmount = extraData.credit_amount || transaction.amount
      return {
        text: `+$${creditAmount.toFixed(2)}`,
        isPositive: true,
      }
    }

    // For downloads (free), show as $0.00
    if (type === 'download') {
      return {
        text: 'Free',
        isPositive: false,
      }
    }

    // For translations and subscriptions (money spent)
    return {
      text: `-$${transaction.amount.toFixed(2)}`,
      isPositive: false,
    }
  }

  // Get card border/bg class based on type
  const getCardClass = (transaction: Transaction): string => {
    if (isReferralRelated(transaction)) {
      return 'border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-pink-500/5'
    }
    return ''
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Get additional details from extra_data
  const getExtraDetails = (transaction: Transaction): string | null => {
    const extraData = parseExtraData(transaction.extra_data)
    
    // For rewards, show expiry if bonus downloads
    if (transaction.type === 'reward' && extraData.reward_type === 'bonus_downloads' && extraData.expires_at) {
      const expiryDate = new Date(extraData.expires_at)
      return `Expires: ${expiryDate.toLocaleDateString()}`
    }
    
    // For downloads, show book title
    if (transaction.type === 'download' && extraData.book_title) {
      return extraData.book_title
    }

    return null
  }

  return (
    <Card className="transaction-history-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Transaction History</CardTitle>
            <CardDescription>View your payment, rewards, and activity history</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadTransactions} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button
            variant={filter === 'reward' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('reward')}
            className={filter === 'reward' ? '' : 'text-purple-600 border-purple-500/30 hover:bg-purple-500/10'}
          >
            <Gift className="h-3 w-3 mr-1" />
            Rewards
          </Button>
          <Button
            variant={filter === 'credit_purchase' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('credit_purchase')}
          >
            <CreditCard className="h-3 w-3 mr-1" />
            Purchases
          </Button>
          <Button
            variant={filter === 'translation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('translation')}
          >
            <BookOpen className="h-3 w-3 mr-1" />
            Translations
          </Button>
          <Button
            variant={filter === 'subscription' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('subscription')}
          >
            <Crown className="h-3 w-3 mr-1" />
            Subscriptions
          </Button>
          <Button
            variant={filter === 'download' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('download')}
          >
            <Download className="h-3 w-3 mr-1" />
            Downloads
          </Button>
        </div>

        <Separator className="mb-4" />

        {/* Transactions List */}
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No transactions found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const displayValue = getDisplayValue(transaction)
              const extraDetails = getExtraDetails(transaction)
              
              return (
                <Card key={transaction.id} className={cn("transaction-item", getCardClass(transaction))}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={cn("icon-wrapper p-2 rounded-lg", getIconBgClass(transaction))}>
                          {getTypeIcon(transaction)}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{getTypeName(transaction)}</p>
                            {getStatusBadge(transaction.status || 'unknown')}
                          </div>
                          {transaction.description && (
                            <p className="text-sm text-muted-foreground">
                              {transaction.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                            <span>{formatDate(transaction.created_at)}</span>
                            {extraDetails && (
                              <span className="text-purple-600">{extraDetails}</span>
                            )}
                            {transaction.tokens_used && transaction.tokens_used > 0 && (
                              <span>{transaction.tokens_used.toLocaleString()} tokens</span>
                            )}
                            {transaction.free_tokens_applied && transaction.free_tokens_applied > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {transaction.free_tokens_applied.toLocaleString()} free tokens
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "text-lg font-bold",
                          displayValue.isPositive 
                            ? isBonusDownloadReward(transaction) 
                              ? "text-purple-600" 
                              : "text-green-600"
                            : "text-foreground"
                        )}>
                          {displayValue.text}
                        </p>
                        {transaction.currency && transaction.amount > 0 && !isBonusDownloadReward(transaction) && (
                          <p className="text-xs text-muted-foreground">{transaction.currency}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
